/* eslint-disable import/no-cycle */
import { StepperSelectionEvent } from '@angular/cdk/stepper'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  linkedSignal,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'

import { orderBy as _orderBy } from 'lodash/fp'

import { tap } from 'rxjs'

import { FlxThemeModule } from '@flowx/angular-theme'
import { FlxStepperModule } from '@flowx/angular-ui-toolkit'
import {
  ComponentIdentifierEnum,
  StepTemplateConfig,
  StepperTemplateConfig,
  getCurrentStepIndex$,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxTreeNodeComponent } from '../../renderer/tree-node/tree-node.component'
import { emitScreenAnalyticsOnDisplay } from '../../utils/screen-analytics.utils'
import { getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-stepper-wrapper',
  imports: [
    ReactiveFormsModule,
    FlxTreeNodeComponent,
    FlxLocalizePipe,
    FlxStepperModule,
    FlxThemeModule,
  ],
  templateUrl: './stepper-wrapper.component.html',
  styleUrl: './stepper-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxStepperWrapperComponent implements OnInit {
  #injector = inject(Injector)
  #destroyRef = inject(DestroyRef)

  templateConfig = input.required<StepperTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  currentStepIndex: Signal<number>

  steps = linkedSignal(() => {
    const { templateConfig } = this.templateConfig()
    if (!templateConfig) {
      return []
    }
    const currentStepIndex = untracked(() => this.currentStepIndex?.()) ?? 0
    const a11yIdex = crypto.randomUUID()
    return _orderBy(
      'order',
      'asc',
      templateConfig.filter((tc) => tc.componentIdentifier === ComponentIdentifierEnum.STEP)
    ).map((step, index) => ({
      ...step,
      completed: index < currentStepIndex,
      a11yIndex: `${a11yIdex}-${index + 1}`,
      ariaLabel: (step as StepTemplateConfig).displayOptions.flowxProps.accessibility?.label,
    }))
  })

  dynamicLabels = signal([])

  stepsForm = computed(() => {
    const form = new FormGroup({})
    this.steps().forEach((step) =>
      form.addControl(`${step.id}`, new FormControl(null, [Validators.required]))
    )
    return form
  })

  selectedIndex = signal(0)

  ngOnInit(): void {
    // Navigation areas are screen roots in UI flows and emit the same
    // SCREEN event a user task root container does.
    emitScreenAnalyticsOnDisplay(this.templateConfig(), this.flowxProps(), this.#destroyRef)
    runInInjectionContext(this.#injector, () => {
      const { templateConfig } = this.templateConfig()
      this.currentStepIndex = toSignal(
        getCurrentStepIndex$(this.steps()).pipe(
          tap((currentStepIndex) => {
            this.#updateStepsValidity(currentStepIndex)
            this.#markPreviousStepsAsCompleted(currentStepIndex)
            this.selectedIndex.set(currentStepIndex)
          })
        )
      )
      this.dynamicLabels.set(
        _orderBy(
          'order',
          'asc',
          templateConfig
            .filter((tc) => tc.componentIdentifier === ComponentIdentifierEnum.STEP)
            .map((step) => getDynamicPropSignal(step.displayOptions.flowxProps.label, step))
        )
      )
    })
  }

  getFormControl(step: StepTemplateConfig): AbstractControl {
    return this.stepsForm().get(`${step.id}`)
  }

  stepClicked(event: StepperSelectionEvent): void {
    this.selectedIndex.set(event.selectedIndex)
  }

  #updateStepsValidity(selectedIndex: number): void {
    this.steps().forEach((step, index) => {
      if (index < selectedIndex) {
        this.getFormControl(step).setValue(step.id)
      } else {
        this.getFormControl(step).reset(null)
      }
      this.stepsForm().updateValueAndValidity()
    })
  }

  #markPreviousStepsAsCompleted(selectedIndex: number): void {
    this.steps.update((steps) =>
      steps.map((step, i) => ({ ...step, completed: i < selectedIndex }))
    )
  }
}
